/**
 * React Query hooks for the admin Customer Service screen. The actual
 * fetchers + types live in `@/api/adminSupport`; this file just wires
 * caching/invalidation conventions consistent with the rest of the
 * admin console.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AdjustBalanceInput,
  adjustBalance,
  fetchDeviceDetail,
  fetchPublisherDetail,
  fetchSupportOverview,
  searchSupport,
  SearchParams
} from '@/api/adminSupport';

export function useSupportOverview() {
  return useQuery({
    queryKey: ['adminSupportOverview'],
    queryFn: fetchSupportOverview,
    refetchInterval: 120_000
  });
}

export function useSupportSearch(params: SearchParams, enabled: boolean) {
  return useQuery({
    queryKey: ['adminSupportSearch', params],
    queryFn: () => searchSupport(params),
    enabled: enabled && params.q.length >= 2,
    placeholderData: prev => prev
  });
}

export function useSupportDevice(id: string | null) {
  return useQuery({
    queryKey: ['adminSupportDevice', id],
    queryFn: () => fetchDeviceDetail(id!),
    enabled: Boolean(id)
  });
}

export function useSupportPublisher(id: string | null) {
  return useQuery({
    queryKey: ['adminSupportPublisher', id],
    queryFn: () => fetchPublisherDetail(id!),
    enabled: Boolean(id)
  });
}

export function useAdjustBalance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AdjustBalanceInput) => adjustBalance(input),
    onSuccess: (_data, vars) => {
      // Re-pull the device so the new balance shows up immediately.
      qc.invalidateQueries({ queryKey: ['adminSupportDevice', vars.deviceId] });
    }
  });
}
